<script setup lang="ts">
import type { DropdownMenuItem } from '@nuxt/ui'
import { computed, shallowRef } from 'vue'
import {
  THEME_PREFERENCES,
  themePreferenceIcon,
  useThemePreference
} from '~/shared-kernel/presentation/composables/useThemePreference'

const { t } = useI18n()
const menuOpen = shallowRef(false)
const { preference, setThemePreference } = useThemePreference()

const currentLabel = computed(() => t(`theme.${preference.value}`))
const triggerLabel = computed(() => t('theme.selectLabel', {
  current: currentLabel.value
}))
const menuItems = computed<DropdownMenuItem[][]>(() => [
  THEME_PREFERENCES.map(mode => ({
    label: t(`theme.${mode}`),
    icon: themePreferenceIcon(mode),
    type: 'checkbox' as const,
    checked: preference.value === mode,
    onSelect: () => setThemePreference(mode)
  }))
])
</script>

<template>
  <UDropdownMenu
    v-model:open="menuOpen"
    :modal="false"
    :items="menuItems"
    :content="{ align: 'end', sideOffset: 8, collisionPadding: 12, avoidCollisions: true }"
    :ui="{ content: 'theme-preference-menu', viewport: 'min-h-0' }"
  >
    <button
      type="button"
      class="theme-preference-selector"
      aria-haspopup="menu"
      :aria-expanded="menuOpen"
      :aria-label="triggerLabel"
      :title="triggerLabel"
    >
      <UIcon :name="themePreferenceIcon(preference)" aria-hidden="true" />
      <span>{{ t('theme.label') }}</span>
      <strong>{{ currentLabel }}</strong>
      <UIcon name="i-lucide-chevron-down" aria-hidden="true" />
    </button>
  </UDropdownMenu>
</template>

<style scoped>
/* The portaled choices must stay above sticky navigation and the mobile dock. */
:global(.theme-preference-menu) {
  z-index: 60;
  max-height: min(20rem, var(--reka-dropdown-menu-content-available-height, calc(100dvh - 1.5rem)));
}

.theme-preference-selector {
  display: inline-flex;
  min-height: 2.75rem;
  align-items: center;
  justify-content: center;
  gap: .42rem;
  border: 1px solid var(--tq-border);
  border-radius: var(--tq-radius-md);
  background: color-mix(in oklch, var(--tq-bg-muted) 72%, var(--tq-bg-elevated));
  color: var(--tq-text);
  padding: .55rem .68rem;
  cursor: pointer;
  transition:
    border-color 180ms var(--tq-ease-out),
    background 180ms var(--tq-ease-out),
    transform 180ms var(--tq-ease-out);
}

.theme-preference-selector:hover,
.theme-preference-selector:focus-visible {
  border-color: color-mix(in oklch, var(--tq-color-teal) 42%, var(--tq-border));
  background: color-mix(in oklch, var(--tq-color-teal) 10%, var(--tq-bg-elevated));
  transform: translateY(-1px);
}

.theme-preference-selector:focus-visible {
  outline: 2px solid var(--tq-focus);
  outline-offset: 2px;
}

.theme-preference-selector > :deep(.iconify) {
  width: 1rem;
  height: 1rem;
  flex: 0 0 auto;
}

.theme-preference-selector > :deep(.iconify:first-child) {
  color: var(--tq-teal-readable);
}

.theme-preference-selector span {
  color: var(--tq-text-muted);
  font-size: .78rem;
}

.theme-preference-selector strong {
  font-size: .8rem;
  font-weight: 700;
}

@media (max-width: 42rem) {
  .theme-preference-selector {
    width: 100%;
  }
}
</style>
